import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';
import LocalizedNavLabel from './LocalizedNavLabel';

const menuItems = [
  { label: '首頁', path: '/' },
  { label: '品牌介紹', path: '/about' },
  { label: '合作通路', path: '/stores' },
  { label: '常見問答', path: '/faq' },
  { label: '閱讀推薦', path: '/blog' },
];

const logoUrl =
  'https://storage.readdy-site.link/project_files/fb9a032b-f905-4139-b305-3a12a0952c6a/59b69cfd-0953-4f81-8590-a303a1311dbd_compressed_unnamed.webp';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleNav = (path: string) => {
    navigate(path);
  };

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/home') return true;
    return location.pathname === path;
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ease-out ${
        scrolled
          ? 'h-12 md:h-14 bg-background-50/80 backdrop-blur-[18px] border-b border-primary-500/15'
          : 'h-14 md:h-16 bg-transparent'
      }`}
    >
      <div className="mx-auto flex items-center h-full justify-between">
        {/* Logo */}
        <button
          onClick={() => handleNav('/')}
          className="flex items-center gap-2.5 pl-6 md:pl-10 cursor-pointer whitespace-nowrap shrink-0"
          aria-label="機曜文化 Giyao 首頁"
        >
          <img
            src={logoUrl}
            alt="機曜文化 Giyao"
            className={`object-contain transition-all duration-400 ease-out ${
              scrolled ? 'h-6 md:h-7' : 'h-7 md:h-8'
            }`}
          />
          <span
            className={`hidden sm:block font-heading tracking-widest transition-all duration-400 ease-out ${
              scrolled
                ? 'text-foreground-100 text-xs'
                : 'text-foreground-100 text-sm font-medium'
            }`}
          >
            機曜文化
          </span>
        </button>

        {/* Desktop menu + external links */}
        <div className="hidden md:flex items-center h-full">
          {menuItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNav(item.path)}
              className={`h-full px-4 lg:px-5 transition-all duration-300 ease-out whitespace-nowrap cursor-pointer relative ${
                scrolled
                  ? `text-sm text-foreground-300 hover:text-primary-400 ${
                      isActive(item.path)
                        ? 'text-primary-400'
                        : ''
                    }`
                  : `text-sm text-foreground-300 hover:text-primary-400 ${
                      isActive(item.path)
                        ? 'text-primary-400'
                        : ''
                    }`
              }`}
            >
              <LocalizedNavLabel href={item.path} fallback={item.label} />
              {isActive(item.path) && (
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-[2px] bg-primary-500 shadow-[0_0_8px_var(--tw-shadow-color)] shadow-primary-500/60" />
              )}
            </button>
          ))}

          {/* External site links — subtle separator + links */}
          <span className="w-px h-4 bg-foreground-800 mx-3" />
          <a
            href="https://sonbook.net/"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className={`h-full flex items-center gap-1 px-3 transition-all duration-300 ease-out whitespace-nowrap cursor-pointer ${
              scrolled
                ? 'text-xs text-foreground-500 hover:text-foreground-300'
                : 'text-xs text-foreground-500 hover:text-foreground-300'
            }`}
          >
            崧博出版
            <i className="ri-arrow-right-up-line text-[10px]" />
          </a>
          <a
            href="https://catalog.sonbook.net/"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className={`h-full flex items-center gap-1 px-3 transition-all duration-300 ease-out whitespace-nowrap cursor-pointer ${
              scrolled
                ? 'text-xs text-foreground-500 hover:text-foreground-300'
                : 'text-xs text-foreground-500 hover:text-foreground-300'
            }`}
          >
            書籍資料庫
            <i className="ri-arrow-right-up-line text-[10px]" />
          </a>
        </div>

        <LanguageSwitcher />

        {/* Mobile toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden w-14 h-full flex items-center justify-center cursor-pointer transition-colors duration-200 text-foreground-200 hover:text-primary-400"
          aria-label="Toggle menu"
        >
          <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
        </button>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div className="md:hidden border-t border-primary-500/10 bg-background-50/95 backdrop-blur-[18px]">
          {menuItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNav(item.path)}
              className={`flex items-center w-full px-6 py-4 text-sm font-medium cursor-pointer border-b border-foreground-900/10 transition-colors duration-200 ${
                isActive(item.path)
                  ? 'text-primary-400 bg-primary-500/8'
                  : 'text-foreground-300 hover:text-primary-400 hover:bg-primary-500/4'
              }`}
            >
              <LocalizedNavLabel href={item.path} fallback={item.label} />
              {isActive(item.path) && (
                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-500 shadow-[0_0_6px_var(--tw-shadow-color)] shadow-primary-500/60" />
              )}
            </button>
          ))}
          {/* External site links in mobile menu */}
          <div className="px-6 py-3 border-b border-foreground-900/10">
            <span className="text-[10px] font-medium text-foreground-600 tracking-[0.25em] uppercase font-label block mb-2">
              相關網站
            </span>
            <div className="flex flex-col gap-1">
              <a
                href="https://sonbook.net/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center gap-2 py-2 text-sm text-foreground-400 hover:text-primary-400 transition-colors duration-200"
              >
                崧博出版
                <i className="ri-arrow-right-up-line text-[10px] text-foreground-600" />
              </a>
              <a
                href="https://catalog.sonbook.net/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center gap-2 py-2 text-sm text-foreground-400 hover:text-primary-400 transition-colors duration-200"
              >
                書籍資料庫
                <i className="ri-arrow-right-up-line text-[10px] text-foreground-600" />
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
